import React, { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, Search, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGetDailyAttendanceReportQuery } from "../../slices/attendanceApiSlice";
import { useGetDivisionsQuery } from "../../slices/userApiSlice";
import LateReasonDialog from "../../components/LateReasonDialog/LateReasonDialog";
import LocationCell from "../../components/Attendance/LocationCell";

export default function SupervisorEmployeeAttendance() {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const divisionId = userInfo?.divisionId;
  const academyId = userInfo?.academyId;

  // Fetch divisions - shared across all academies
  const { data: divisionsData } = useGetDivisionsQuery();
  const divisions = divisionsData || [];

  const [activeTab, setActiveTab] = useState("clocked_in");
  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [showLateReasonDialog, setShowLateReasonDialog] = useState(false);
  const [selectedLateReason, setSelectedLateReason] = useState("");
  const pageSize = 20;

  // Map tab to status
  const statusMap = {
    clocked_in: "clocked_in",
    clocked_out: "clocked_out",
    absentees: "absent",
    on_leave: "on_leave",
  };

  // Fetch daily attendance report
  const {
    data: reportData,
    isLoading,
    isFetching,
    error,
  } = useGetDailyAttendanceReportQuery({
    report_type: "division",
    status: statusMap[activeTab],
    page: currentPage,
    size: pageSize,
  });

  // Filter data based on search query, academy ID, division ID, and tab
  const filteredContent = useMemo(() => {
    if (!reportData?.content) return [];

    let filtered = reportData.content;

    // Filter by academy ID (must match user's academy)
    if (academyId) {
      filtered = filtered.filter(
        (employee) => employee.academyId === academyId,
      );
    }

    // Filter by division ID (must match user's division)
    if (divisionId) {
      filtered = filtered.filter(
        (employee) => employee.divisionId === divisionId,
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((employee) => {
        const fullName = `${employee.firstName || ""} ${employee.lastName || ""
          }`.toLowerCase();
        const email = (employee.email || "").toLowerCase();
        const employeeId = (employee.employeeId || "").toLowerCase();
        return (
          fullName.includes(query) ||
          email.includes(query) ||
          employeeId.includes(query)
        );
      });
    }

    // Extra safety: filter by tab semantics so rows don't leak between tabs
    filtered = filtered.filter((employee) => {
      if (activeTab === "clocked_in") {
        return !!employee.clockInTime;
      }
      if (activeTab === "clocked_out") {
        return !!employee.clockOutTime;
      }
      if (activeTab === "on_leave") {
        return !!employee.leaveDays;
      }
      if (activeTab === "absentees") {
        return (
          !employee.clockInTime && !employee.clockOutTime && !employee.leaveDays
        );
      }
      return true;
    });

    // Deduplicate by user per tab (avoid duplicates when API returns multiple)
    const uniqueMap = new Map();
    for (const emp of filtered) {
      const baseKey =
        emp.userId || emp.employeeId || emp.email || emp.attendanceId;
      if (!baseKey) continue;
      const key = `${activeTab}-${baseKey}`;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, emp);
      }
    }

    return Array.from(uniqueMap.values());
  }, [reportData, searchQuery, academyId, divisionId, activeTab]);

  // Format time - backend stores Thimphu time as UTC, so we display it directly
  const formatTime = (timeString) => {
    if (!timeString) return "-";
    try {
      return new Date(timeString).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Asia/Thimphu",
      });
    } catch {
      return timeString;
    }
  };

  // Get division name by ID
  const getDivisionName = (divisionId) => {
    if (!divisionId || !divisions.length) return "N/A";
    const division = divisions.find(
      (div) => div.divisionId === divisionId || div._id === divisionId,
    );
    return division?.name || "N/A";
  };


  // Get user initials for avatar
  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0)?.toUpperCase() || "";
    const last = lastName?.charAt(0)?.toUpperCase() || "";
    return first + last || "U";
  };

  // Get full name
  const getFullName = (firstName, lastName) => {
    return `${firstName || ""} ${lastName || ""}`.trim() || "Unknown";
  };

  const handleBack = () => {
    navigate("/supervisor/attendance");
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0); // Reset to first page when changing tabs
    setSearchQuery(""); // Clear search when changing tabs
  };

  // Calculate pagination based on filtered content (frontend filtering)
  const totalFilteredElements = filteredContent.length;
  const totalPages = Math.ceil(totalFilteredElements / pageSize);
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedContent = filteredContent.slice(startIndex, endIndex);

  return (
    <div className="p-4 sm:py-6 sm:px-8 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-zinc-600 hover:bg-zinc-100 hover:text-zinc-800 transition-colors"
            aria-label="Go back"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-zinc-800">
            Daily Employee Attendance
          </h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-200 overflow-x-auto">
        <div className="flex space-x-4 sm:space-x-8 min-w-max">
          {[
            { id: "clocked_in", label: "Clocked In" },
            { id: "clocked_out", label: "Clocked Out" },
            { id: "absentees", label: "Absentees" },
            { id: "on_leave", label: "On Leave" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`pb-3 px-1 font-medium text-sm transition-colors ${activeTab === tab.id
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-zinc-600 hover:text-zinc-800"
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative w-full sm:max-w-xs">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-500 text-sm"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider hidden sm:table-cell">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider hidden sm:table-cell">
                  Division
                </th>
                {activeTab !== "absentees" && activeTab !== "on_leave" && (
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                    Location
                  </th>
                )}
                {activeTab !== "absentees" && activeTab !== "on_leave" && (
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                    {activeTab === "clocked_in"
                      ? "Clocked in at"
                      : "Clocked out at"}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-zinc-200">
              {isLoading || isFetching ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={`skeleton-${idx}`}>
                    <td
                      colSpan={
                        activeTab !== "absentees" && activeTab !== "on_leave"
                          ? 5
                          : 3
                      }
                      className="px-6 py-4"
                    >
                      <div className="animate-pulse flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-zinc-200" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 bg-zinc-200 rounded w-1/3" />
                          <div className="h-3 bg-zinc-200 rounded w-1/4" />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td
                    colSpan={activeTab === "absentees" ? 4 : 5}
                    className="px-6 py-8 text-center text-red-500"
                  >
                    Error loading data. Please try again.
                  </td>
                </tr>
              ) : filteredContent.length === 0 ? (
                <tr>
                  <td
                    colSpan={activeTab === "absentees" ? 4 : 5}
                    className="px-6 py-8 text-center text-zinc-500"
                  >
                    No records found
                  </td>
                </tr>
              ) : (
                paginatedContent.map((employee) => (
                  <tr
                    key={employee.userId}
                    className="hover:bg-zinc-50 cursor-pointer"
                    onClick={() => {
                      if (employee.attendanceId) {
                        navigate(
                          `/supervisor/attendance/record/${employee.attendanceId}`,
                        );
                      }
                    }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="relative inline-block">
                          {employee.profileImageUrl ? (
                            <img
                              src={employee.profileImageUrl}
                              alt={getFullName(
                                employee.firstName,
                                employee.lastName,
                              )}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-zinc-300 flex items-center justify-center">
                              <span className="text-zinc-600 font-semibold text-sm">
                                {getInitials(
                                  employee.firstName,
                                  employee.lastName,
                                )}
                              </span>
                            </div>
                          )}
                          {employee.lateReason && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedLateReason(employee.lateReason);
                                setShowLateReasonDialog(true);
                              }}
                              className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-full hover:bg-orange-100 transition-colors shadow-sm whitespace-nowrap z-10"
                              title="Late arrival"
                            >
                              <Clock size={12} />
                              Late
                            </button>
                          )}
                        </div>
                        {/* Mobile: show name and division stacked next to avatar */}
                        <div className="ml-3 sm:hidden">
                          <div className="text-sm font-medium text-zinc-900">
                            {getFullName(employee.firstName, employee.lastName)}
                          </div>
                          <div className="text-xs text-zinc-500">
                            {getDivisionName(employee.divisionId)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                      <div className="text-sm font-medium text-zinc-900">
                        {getFullName(employee.firstName, employee.lastName)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                      <div className="text-sm text-zinc-600">
                        {getDivisionName(employee.divisionId)}
                      </div>
                    </td>
                    {activeTab !== "absentees" && activeTab !== "on_leave" && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <LocationCell
                          location={
                            activeTab === "clocked_in"
                              ? employee.clockInLocation
                              : employee.clockOutLocation
                          }
                        />
                      </td>
                    )}
                    {activeTab !== "absentees" && activeTab !== "on_leave" && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-zinc-600">
                          {activeTab === "clocked_in"
                            ? formatTime(employee.clockInTime)
                            : formatTime(employee.clockOutTime)}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
                className="p-2 rounded-lg border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 7) {
                    pageNum = i;
                  } else if (currentPage <= 3) {
                    pageNum = i;
                  } else if (currentPage >= totalPages - 4) {
                    pageNum = totalPages - 7 + i;
                  } else {
                    pageNum = currentPage - 3 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                        ? "bg-zinc-900 text-white"
                        : "text-zinc-600 hover:bg-zinc-100"
                        }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
                {totalPages > 7 && currentPage < totalPages - 4 && (
                  <>
                    <span className="px-2 text-zinc-400">...</span>
                    <button
                      onClick={() => setCurrentPage(totalPages - 1)}
                      className="px-3 py-1 rounded-lg text-sm font-medium text-zinc-600 hover:bg-zinc-100 transition-colors"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))
                }
                disabled={currentPage === totalPages - 1}
                className="p-2 rounded-lg border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="text-sm text-zinc-600 font-medium">
              {totalFilteredElements} Results
            </div>
          </div>
        )}
      </div>
      <LateReasonDialog
        show={showLateReasonDialog}
        onClose={() => {
          setShowLateReasonDialog(false);
          setSelectedLateReason("");
        }}
        lateReason={selectedLateReason}
      />
    </div>
  );
}
